import {
  IRawStatsPublisher,
  IRawStatsSubscriber,
  IStats,
  IStatsPublisher,
  IStatsSubscriber,
} from 'interfaces/statistics';
import webSocket from 'lib/socket';
import vendor from 'lib/vendor';
import { action, observable, toJS } from 'mobx';
import { ssm, StoreWithCache } from './store-manager';

const STORAGE_KEY = 'VKYC_STATS';
const GET_NETWORK_STATS_DURATION = 5000;
const SEND_STATS_DURATION = 20000;
const NETWORK_THRESHOLD = 60;

class StatsStore {
  private static STATS_DEFAULT: IStats = {
    s: [],
    p: [],
  };

  @observable
  public subscriberSpeed: number | null = null;

  @observable
  public publisherSpeed: number | null = null;

  @observable
  public subSlow = false;

  @observable
  public pubSlow = false;

  @observable
  public netSlow = false;

  @observable
  public tip = '';

  @observable
  public connection = 'connecting';

  private preStatsSubscriber: IRawStatsSubscriber | null = null;
  private preStatsPublisher: IRawStatsPublisher | null = null;

  private getStatsTimer: any = null;

  private sendStatsTimer: any = null;

  private store: StoreWithCache<IStats>;

  constructor() {
    this.store = ssm.getStoreWithCache<IStats>(STORAGE_KEY);
    this.store.setDefault(() => StatsStore.STATS_DEFAULT);
  }

  public clearSendStatsTimerIfSendNow(ifSendNow?: boolean) {
    clearTimeout(this.sendStatsTimer);
    if (ifSendNow) {
      this.sendStats();
    }
  }

  public sendStats() {
    if (this.hasStats()) {
      webSocket.sendStats(this.getStats());
    }
  }

  public getStats(): IStats {
    return this.store.getNotNull();
  }

  public initSendStatsTimer() {
    if (statsStore.sendStatsTimer) {
      clearTimeout(statsStore.sendStatsTimer);
    }
    statsStore.sendStatsTimer = window.setTimeout(() => {
      webSocket.sendStats(this.getStats());
      this.initSendStatsTimer();
    }, SEND_STATS_DURATION);
  }

  @action
  public updateTip = (data: string) => {
    vendor.vendorLog(data);
    this.tip = data;
  };

  @action
  public updateConnection = (data: string) => {
    vendor.vendorLog(data);
    this.connection = data;
  };

  public initGetStatsTimer = () => {
    if (this.getStatsTimer) {
      clearTimeout(this.getStatsTimer);
    }
    this.getStatsTimer = window.setTimeout(() => {
      vendor.getStatsPublisher();
      vendor.getStatsSubscriber();
      this.updateNetworkMessage();
      this.initGetStatsTimer();
    }, GET_NETWORK_STATS_DURATION);
  };

  public updateNetworkStatsPublisher = (data: IStatsPublisher) => {
    this.store.setMember((v) => v.p.push(data));
  };

  public updateNetworkStatsSubcriber = (data: IStatsSubscriber) => {
    this.store.setMember((v) => v.s.push(data));
  };

  public hasStats = () => {
    const stats = this.store.getNotNull();
    const p = stats.p;
    const s = stats.s;
    return (p && p.length > 0) || (s && s.length > 0);
  };

  @action
  public updateSubcriberSpeed = (data: number | null) => {
    this.subscriberSpeed = data;
    // this.subSlow = data ? data <= NETWORK_THRESHOLD : false;
  };

  @action
  public updatePublisherSpeed = (data: number | null) => {
    this.publisherSpeed = data;
    // this.pubSlow = data ? data <= NETWORK_THRESHOLD : false;
  };

  @action
  public clearStats = () => {
    this.clearSendStatsTimerIfSendNow(false);
    this.clearBothTimer();
    this.store.clear();
    this.preStatsSubscriber = null;
    this.preStatsPublisher = null;
    this.pubSlow = false;
    this.subSlow = false;
    this.netSlow = false;
    this.updatePublisherSpeed(null);
    this.updateSubcriberSpeed(null);
  };

  public updateSubscriberInfo = (current: any) => {
    let speed = null;
    let rawStats = null;
    try {
      if (current) {
        rawStats = {
          video: current.video,
          timestamp: current.timestamp,
        };
        if (this.preStatsSubscriber) {
          if (this.preStatsSubscriber?.timestamp === current.timestamp) return;
          speed = this.calcKPBS(
            current.video.bytesReceived - this.preStatsSubscriber.video.bytesReceived,
            current.timestamp - this.preStatsSubscriber.timestamp,
          );
        }
      }
      this.updateSubcriberSpeed(speed);
      this.updateNetworkStatsSubcriber({
        br: rawStats?.video.bytesReceived,
        fr: rawStats?.video.frameRate,
        pl: rawStats?.video.packetsLost,
        pr: rawStats?.video.packetsReceived,
        t: rawStats?.timestamp,
        s: speed,
      });
      this.preStatsSubscriber = rawStats;
    } catch (e) {
      console.log('updateSubscriberInfo error', e);
    }
  };

  public updatePublisherInfo = (current: any) => {
    let speed = null;
    let rawStats = null;
    try {
      if (current) {
        rawStats = {
          video: current.video,
          timestamp: current.timestamp,
        };
        if (this.preStatsPublisher) {
          if (this.preStatsPublisher?.timestamp === current.timestamp) return;
          speed = this.calcKPBS(
            current.video.bytesSent - this.preStatsPublisher.video.bytesSent,
            current.timestamp - this.preStatsPublisher.timestamp,
          );
        }
      }
      this.updatePublisherSpeed(speed);
      this.updateNetworkStatsPublisher({
        bs: rawStats?.video.bytesSent,
        fr: rawStats?.video.frameRate,
        pl: rawStats?.video.packetsLost,
        ps: rawStats?.video.packetsSent,
        t: rawStats?.timestamp,
        s: speed,
      });
      this.preStatsPublisher = rawStats;
    } catch (e) {
      console.log('updateSubscriberInfo error', e);
    }
  };

  public clearBothTimer = () => {
    clearTimeout(this.getStatsTimer);
  };

  private calcKPBS = (diff: number, duration: number) => {
    if (duration <= 0) return null;
    if (diff < 0) return null;
    return ((diff / duration) * 1000 * 8) / 1024;
  };

  private updateNetworkMessage = () => {
    if (!this.publisherSpeed || !this.subscriberSpeed) {
      return;
    }
    if (this.publisherSpeed <= NETWORK_THRESHOLD || this.subscriberSpeed <= NETWORK_THRESHOLD) {
      const stats = this.store.getNotNull();
      const p = stats.p.slice(-3).map((val: IStatsPublisher) => val.s);
      const s = stats.s.slice(-3).map((val: IStatsSubscriber) => val.s);

      this.subSlow = this.checkLast3(s);
      this.pubSlow = this.checkLast3(p);

      this.netSlow = true; // unknown the reason, but follow the old logic
      // maybe this would be better, need to discuss with the PM
      // this.netSlow = this.checkLast3(s) || this.checkLast3(p);
    } else {
      this.subSlow = false;
      this.pubSlow = false;
      this.netSlow = false;
    }
  };

  private checkLast3 = (arr: any[]) => {
    return arr.every((val: any) => val <= NETWORK_THRESHOLD);
  };
}

const statsStore = new StatsStore();
export default statsStore;

['development', 'staging'].includes(process.env.MODE!) &&
  Object.assign(window, { statsStore, toJS });
